import React, { useEffect, useRef, useState } from "react";
import { GoogleOAuthProvider, GoogleLogin } from "@react-oauth/google";
import { toast } from "sonner";
import client from "@/lib/api";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

let configPromise = null;
const loadConfig = () => (configPromise ||= client.get("/auth/google/config").then((r) => r.data.web_client_id).catch(() => null));

// Google button for the website. role = account type for sign-up; omitted on the login page.
export function GoogleWebSignIn({ role, text = "continue_with", onSignedIn }) {
  const [clientId, setClientId] = useState(undefined);
  const [pendingToken, setPendingToken] = useState(null);
  const busy = useRef(false);
  const box = useRef(null);
  const [width, setWidth] = useState(320);
  const { googleAuth } = useAuth();

  useEffect(() => { loadConfig().then(setClientId); }, []);
  useEffect(() => { if (box.current) setWidth(Math.min(400, Math.max(200, box.current.offsetWidth))); }, [clientId]);

  const send = async (token, chosenRole) => {
    if (busy.current) return;
    busy.current = true;
    try {
      const u = await googleAuth(token, chosenRole ? { role: chosenRole } : {});
      setPendingToken(null);
      onSignedIn(u);
    } catch (err) {
      const st = err.response?.status;
      if (st === 404) setPendingToken(token);
      else toast.error(formatApiError(err.response?.data?.detail) || "Google sign-in failed");
    } finally { busy.current = false; }
  };

  if (!clientId) return null;
  return (
    <GoogleOAuthProvider clientId={clientId}>
      <OrDivider />
      <div ref={box} data-testid="google-web-signin" className="flex justify-center w-full overflow-hidden">
        <GoogleLogin text={text} shape="pill" width={String(width)} onSuccess={(r) => send(r.credential, role)}
          onError={() => toast.error("Google sign-in was cancelled or could not start.")} />
      </div>
      <Dialog open={!!pendingToken} onOpenChange={(o) => !o && setPendingToken(null)}>
        <DialogContent data-testid="google-role-dialog">
          <DialogHeader>
            <DialogTitle>No account found</DialogTitle>
            <DialogDescription>No Emira account uses this Google email yet. Create one as:</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Button data-testid="google-role-candidate" onClick={() => send(pendingToken, "candidate")} className="bg-[#0088FF] hover:bg-[#0070E0]">Job seeker</Button>
            <Button data-testid="google-role-agency" variant="outline" onClick={() => send(pendingToken, "agency")}>Agency</Button>
          </div>
        </DialogContent>
      </Dialog>
    </GoogleOAuthProvider>
  );
}

function OrDivider() {
  return (
    <div className="flex items-center gap-3 my-5 text-xs text-slate-400">
      <span className="flex-1 h-px bg-slate-200" />or<span className="flex-1 h-px bg-slate-200" />
    </div>
  );
}
